// frontend/src/utils/authErrorHandler.ts
/**
 * Centralized auth error handling utilities.
 * Used to detect and handle invalid refresh token errors gracefully.
 */

import { SUPABASE_AUTH_TOKEN_KEY, AUTH_STORAGE_KEY, PKCE_CODE_VERIFIER_KEY } from '../lib/supabase';
import { dispatchSignoutRequired } from './appEvents';

// Flag to track if logout is in progress - prevents error cascade during intentional signout
let isLoggingOut = false;

/**
 * Mark that logout is in progress. Call this before signing out.
 * This prevents 401 errors during logout from triggering additional error handling.
 */
export const setLoggingOut = (value: boolean): void => {
  isLoggingOut = value;
};

/**
 * Check if an error is a TEMPORARY refresh failure.
 * These should NOT trigger signout - TokenManager handles retry.
 */
export const isTemporaryRefreshError = (error: { message?: string } | null | undefined): boolean => {
  if (!error) return false;

  const message = (error?.message || '').toLowerCase();

  // "already used" means another request refreshed first - just retry
  return message.includes('refresh token already used');
};

/**
 * localStorage keys that should be cleared on auth errors.
 */
export const AUTH_STORAGE_KEYS = [
  AUTH_STORAGE_KEY,
  // Clearing auth state must include the PKCE code verifier, or a stale one
  // outlives the session it belonged to.
  PKCE_CODE_VERIFIER_KEY,
  'academic_planner_beta_auth',
  SUPABASE_AUTH_TOKEN_KEY,
  'academic_planner_db_user',
  'academic_planner_setup_dismissed',
  'chatbot-enabled',
  'collapsedSemesters',
  'bookmarkedCollapsed',
  'commandHistory'
];

/**
 * Clear all auth-related storage.
 */
export const clearAuthStorage = () => {
  AUTH_STORAGE_KEYS.forEach(key => localStorage.removeItem(key));
};

// Flag to prevent multiple simultaneous auth error handling
let isHandlingAuthError = false;

/**
 * Handle an invalid refresh token error by clearing storage and triggering signout.
 * This dispatches a custom event that AuthContext listens for.
 *
 * Note: This is a no-op if logout is already in progress (to prevent error cascade).
 */
export const handleAuthError = async () => {
  // Skip if we're already logging out - prevents cascade of errors during intentional signout
  if (isLoggingOut) {
    return;
  }

  // Prevent multiple simultaneous auth error handling
  if (isHandlingAuthError) {
    return;
  }
  isHandlingAuthError = true;

  console.warn('Auth error detected - clearing session');
  clearAuthStorage();

  // Also clear TokenManager cache if available
  try {
    const { clearTokenCache } = await import('../lib/tokenManager');
    clearTokenCache();
  } catch {
    // TokenManager may not be loaded yet
  }

  // Dispatch custom event for AuthContext to handle
  dispatchSignoutRequired({ reason: 'invalid_refresh_token' });

  // Reset flag after a short delay to allow for recovery
  setTimeout(() => {
    isHandlingAuthError = false;
  }, 1000);
};
